import React, { useState, useEffect } from 'react';
import { Mail, Trash2 } from 'lucide-react';


const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function Messages() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [thread, setThread] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    body: '',
    sendToAll: true,
    recipientHostIds: []
  });
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchConversations();
    fetchHosts();
  }, []);

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('quizzler_admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setLoading(false);
    }
  };

  const fetchThread = async (messageId) => {
    try {
      const token = localStorage.getItem('quizzler_admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/messages/${messageId}/thread`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setThread(data.thread || []);
      }
    } catch (error) {
      console.error('Error fetching thread:', error);
    }
  };

  const fetchHosts = async () => {
    try {
      const token = localStorage.getItem('quizzler_admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/hosts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setHosts(data.hosts || []);
      }
    } catch (error) {
      console.error('Error fetching hosts:', error);
    }
  };

  const handleConversationClick = (conversation) => {
    setSelectedConversation(conversation);
    setReplyText('');
    fetchThread(conversation.id);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    setError('');
    setSending(true);

    try {
      const token = localStorage.getItem('quizzler_admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        alert(`✅ ${data.message}`);
        setFormData({
          subject: '',
          body: '',
          sendToAll: true,
          recipientHostIds: []
        });
        setShowCompose(false);
        fetchConversations();
      } else {
        setError(data.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Send message error:', error);
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;

    setSending(true);
    try {
      const token = localStorage.getItem('quizzler_admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/messages/${selectedConversation.id}/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ body: replyText })
      });

      if (response.ok) {
        alert('✅ Reply sent!');
        setReplyText('');
        fetchThread(selectedConversation.id);
        fetchConversations();
      } else {
        alert('Failed to send reply');
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Failed to send reply');
    } finally {
      setSending(false);
    }
  };
  
  const handleDeleteConversation = async (messageId) => {
  if (!window.confirm('Are you sure you want to delete this entire conversation? This cannot be undone and will delete all replies.')) {
    return;
  }

  try {
    const token = localStorage.getItem('quizzler_admin_token');
    const response = await fetch(`${BACKEND_URL}/api/admin/conversations/${messageId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (response.ok) {
      alert('✅ Conversation deleted');
      setConversations(conversations.filter(c => c.id !== messageId));
      if (selectedConversation?.id === messageId) {
        setSelectedConversation(null);
        setThread([]);
      }
    } else {
      alert('Failed to delete conversation');
    }
  } catch (error) {
    console.error('Error deleting conversation:', error);
    alert('Failed to delete conversation');
  }
};

  const toggleHostSelection = (hostId) => {
    if (formData.recipientHostIds.includes(hostId)) {
      setFormData({
        ...formData,
        recipientHostIds: formData.recipientHostIds.filter(id => id !== hostId)
      });
    } else {
      setFormData({
        ...formData,
        recipientHostIds: [...formData.recipientHostIds, hostId]
      });
    }
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Messages & Conversations</h1>
        <button
          onClick={() => setShowCompose(!showCompose)}
          className="bg-slate-600 hover:bg-slate-700 text-white px-6 py-3 rounded-lg font-semibold transition"
        >
          {showCompose ? 'Cancel' : '<><Mail size={16} className="inline mr-1" /> New Message</>'}
        </button>
      </div>

      {/* Compose Form */}
      {showCompose && (
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Compose Message</h2>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSendMessage}>
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.sendToAll}
                  onChange={(e) => setFormData({ ...formData, sendToAll: e.target.checked })}
                  className="mr-2 h-4 w-4"
                />
                <span className="font-semibold text-gray-700">Send to All Hosts</span>
              </label>
            </div>

            {!formData.sendToAll && (
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Select Recipients
                </label>
                <div className="border border-gray-300 rounded-lg p-4 max-h-48 overflow-y-auto">
                  {hosts.map((host) => (
                    <label key={host.id} className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        checked={formData.recipientHostIds.includes(host.id)}
                        onChange={() => toggleHostSelection(host.id)}
                        className="mr-2 h-4 w-4"
                      />
                      <span>{host.name} ({host.email})</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Subject *
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Championship Update"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Message *
              </label>
              <textarea
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="Your message to hosts..."
                rows="6"
                required
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              className="w-full bg-slate-600 hover:bg-slate-700 text-white font-bold py-3 px-4 rounded-lg transition disabled:opacity-50"
            >
              {sending ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      )}

      {/* Conversations */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conversation List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="bg-slate-600 text-white px-4 py-3 font-semibold">
              Conversations
            </div>

            {loading ? (
              <div className="p-4 text-center text-gray-500">Loading...</div>
            ) : conversations.length === 0 ? (
              <div className="p-4 text-center text-gray-500">No conversations</div>
            ) : (
              <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
                {conversations.map((conv) => (
                  <button
  key={conv.id}
  onClick={() => handleConversationClick(conv)}
  className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition ${
    selectedConversation?.id === conv.id ? 'bg-slate-50' : ''
  }`}
>
  <div className="flex items-center justify-between mb-1">
    <span className="font-semibold text-sm text-gray-800">
      {conv.subject}
    </span>
    <div className="flex items-center gap-2">
      {conv.is_from_host && (
        <span className="bg-teal-100 text-teal-700 text-xs px-2 py-1 rounded">
          From Host
        </span>
      )}
      <button
  onClick={(e) => {
    e.stopPropagation();
    handleDeleteConversation(conv.id);
  }}
  className="text-red-500 hover:text-red-700"
  title="Delete conversation"
>
  <Trash2 size={16} />
</button>
    </div>
  </div>
  <div className="text-xs text-gray-600">
    {conv.is_from_host ? `From: ${conv.sender_name}` : `To: ${conv.recipient_name}`}
  </div>
  <div className="text-xs text-gray-500 mt-1">
    {new Date(conv.created_at).toLocaleDateString()}
    {conv.reply_count > 0 && ` • ${conv.reply_count} replies`}
  </div>
</button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Thread View */}
        <div className="lg:col-span-2">
          {selectedConversation ? (
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                {selectedConversation.subject}
              </h2>

              {/* Thread Messages */}
              <div className="space-y-4 mb-6">
                {thread.map((msg, index) => (
                  <div
                    key={msg.id}
                    className={`p-4 rounded-lg ${
                      msg.sender_type === 'admin'
                        ? 'bg-slate-50 border-l-4 border-slate-600'
                        : 'bg-teal-50 border-l-4 border-teal-600'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-gray-800">
                        {msg.sender_type === 'admin' ? 'Admin' : msg.sender_name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(msg.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="text-gray-700 whitespace-pre-wrap">
                      {msg.body}
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply Form */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="font-bold text-gray-800 mb-3">Reply</h3>
                <form onSubmit={handleReply}>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 mb-3"
                    placeholder="Type your reply..."
                    rows="4"
                    required
                  />
                  <button
                    type="submit"
                    disabled={sending}
                    className="bg-slate-600 hover:bg-slate-700 text-white px-6 py-2 rounded-lg font-semibold transition disabled:opacity-50"
                  >
                    {sending ? 'Sending...' : 'Send Reply'}
                  </button>
                </form>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-md p-6 text-center text-gray-500">
              Select a conversation to view and reply
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
